import React, {
  useState,
  useCallback,
  useContext,
  createContext,
  useMemo,
} from 'react';
import api from 'services';
import { appWarnings } from 'utils/appWarnings';
import { useNewToast } from 'hooks/newToast';
import {
  normalizarLinhasDisponiveisResponse,
  normalizarLinhasLista,
  obterListaFaixasParaPayload,
} from 'pages/Controles/InsertAndUpdatePage/components/costs/components/colheita-por-pe-costs/utils/faixaLinhas';

const RegColheitaPorPeContext = createContext({});

const success = {
  type: 'success',
};

function normalizarListaTalhoes(talhoes) {
  if (Array.isArray(talhoes)) return talhoes;
  if (talhoes) return [talhoes];
  return [];
}

function mergeLinhasDisponiveisPorTalhao(atual, adicionais) {
  const mapa = new Map();
  [...(adicionais ?? []), ...(atual ?? [])].forEach((talhao) => {
    if (talhao?.talhaoId == null || talhao.talhaoId === '') return;
    const key = Number(talhao.talhaoId);
    mapa.set(key, talhao);
  });
  return Array.from(mapa.values());
}

export function RegColheitaPorPeProvider({ children }) {
  const { showToast } = useNewToast();

  const [regMdobrasColheitaPorPe, setRegMdobrasColheitaPorPe] = useState([]);
  const [regMdobrasColheitaPorPeLoading, setRegMdobrasColheitaPorPeLoading] =
    useState(false);
  const [regMdobraColheitaPorPeLoading, setRegMdobraColheitaPorPeLoading] =
    useState(false);
  const [
    removeRegMdobrasColheitaPorPeLoading,
    setRemoveRegMdobrasColheitaPorPeLoading,
  ] = useState(false);
  const [linhasDisponiveis, setLinhasDisponiveis] = useState([]);
  const [linhasDisponiveisLoading, setLinhasDisponiveisLoading] =
    useState(false);

  const getRegMdobrasColheitaPorPe = useCallback(async (registroId) => {
    try {
      setRegMdobrasColheitaPorPeLoading(true);
      const response = await api
        .regAtividade()
        .getRegMdobrasColheitaPorPe(registroId);
      setRegMdobrasColheitaPorPe(response);
      setRegMdobrasColheitaPorPeLoading(false);
    } catch (errors) {
      setRegMdobrasColheitaPorPeLoading(false);
    }
  }, []);

  const getLinhasDisponiveisProducao = useCallback(async (atividadeId) => {
    try {
      setLinhasDisponiveisLoading(true);
      const response = await api
        .regAtividade()
        .getLinhasDisponiveisProducao(atividadeId);
      setLinhasDisponiveis(
        normalizarLinhasDisponiveisResponse(
          Array.isArray(response) ? response : [],
        ),
      );
    } catch {
      setLinhasDisponiveis([]);
    } finally {
      setLinhasDisponiveisLoading(false);
    }
  }, []);

  const getLinhasDisponiveisPorTalhoes = useCallback(
    async (talhoes, options = {}) => {
      const { merge = false } = options;
      const lista = normalizarListaTalhoes(talhoes);
      const talhoesValidos = lista.filter(
        (t) => t?.id && t?.nome !== 'GASTOS GERAIS',
      );

      if (talhoesValidos.length === 0) {
        setLinhasDisponiveis([]);
        return;
      }

      try {
        setLinhasDisponiveisLoading(true);
        const resultados = await Promise.all(
          talhoesValidos.map(async (talhao) => {
            const response = await api
              .talhao()
              .getTalhaoLinhas({ talhaoId: talhao.id });
            const linhasArr = Array.isArray(response) ? response : [];
            if (linhasArr.length === 0) return null;
            return {
              talhaoId: talhao.id,
              nome: talhao.nome,
              numero: talhao.numero ?? String(talhao.id),
              linhas: normalizarLinhasLista(linhasArr),
            };
          }),
        );
        const linhasTalhoes = resultados.filter(Boolean);
        setLinhasDisponiveis((atual) =>
          merge
            ? mergeLinhasDisponiveisPorTalhao(atual, linhasTalhoes)
            : linhasTalhoes,
        );
      } catch {
        setLinhasDisponiveis([]);
      } finally {
        setLinhasDisponiveisLoading(false);
      }
    },
    [],
  );

  const insertRegColheitaPorPe = useCallback(
    async (registroId, data) => {
      try {
        setRegMdobraColheitaPorPeLoading(true);
        const formattedData = {
          vinculos: data?.map((item) => {
            const medidaStr = item?.medida?.value ?? item?.medida ?? 'Planta';
            const colaborador = item?.duplicate
              ? item?.glo_vinculo_id
              : item?.colaborador;

            if (item?.tipoEntrada === 'faixa') {
              const faixasLimpas = obterListaFaixasParaPayload(item);
              const vinculoFaixa = {
                ...(item?.id != null && { id: item.id }),
                colaborador,
                valor: item?.valor,
                medida: medidaStr,
                obs: item?.obs ?? '',
                tipoEntrada: 'faixa',
                tipo: 'UN',
                resto: 0,
              };

              if (faixasLimpas.length >= 2) {
                vinculoFaixa.faixas = faixasLimpas;
              } else if (faixasLimpas.length === 1) {
                vinculoFaixa.talhaoId = faixasLimpas[0].talhaoId;
                vinculoFaixa.linhaInicio = faixasLimpas[0].linhaInicio;
                vinculoFaixa.linhaFim = faixasLimpas[0].linhaFim;
              }

              return vinculoFaixa;
            }

            return {
              id: item?.id ?? undefined,
              colaborador,
              colhido: item?.colhido,
              valor: item?.valor,
              medida: medidaStr,
              obs: item?.obs ?? '',
              tipoEntrada: 'total',
              tipo: 'UN',
              resto: 0,
            };
          }),
        };
        await api
          .regAtividade()
          .insertRegColheitaPorPe(registroId, formattedData);
        getRegMdobrasColheitaPorPe(registroId);
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      } finally {
        setRegMdobraColheitaPorPeLoading(false);
      }
    },
    [showToast, getRegMdobrasColheitaPorPe],
  );

  const removeRegMdobrasColheitaPorPe = useCallback(
    async (activityId, id) => {
      try {
        setRemoveRegMdobrasColheitaPorPeLoading(true);
        await api.regAtividade().removeRegColheitaPorPe(activityId);
        if (id) {
          showToast({
            ...success,
            message: 'Colaborador excluído com sucesso!',
          });
        } else {
          showToast({
            ...success,
            message: 'Custo de colheita por pé excluído com sucesso!',
          });
        }
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      } finally {
        setRemoveRegMdobrasColheitaPorPeLoading(false);
      }
    },
    [showToast],
  );

  return (
    <RegColheitaPorPeContext.Provider
      value={useMemo(
        () => ({
          regMdobrasColheitaPorPe,
          regMdobrasColheitaPorPeLoading,
          regMdobraColheitaPorPeLoading,
          removeRegMdobrasColheitaPorPeLoading,
          linhasDisponiveis,
          linhasDisponiveisLoading,
          getRegMdobrasColheitaPorPe,
          getLinhasDisponiveisProducao,
          getLinhasDisponiveisPorTalhoes,
          insertRegColheitaPorPe,
          removeRegMdobrasColheitaPorPe,
        }),
        [
          regMdobrasColheitaPorPe,
          regMdobrasColheitaPorPeLoading,
          regMdobraColheitaPorPeLoading,
          removeRegMdobrasColheitaPorPeLoading,
          linhasDisponiveis,
          linhasDisponiveisLoading,
          getRegMdobrasColheitaPorPe,
          getLinhasDisponiveisProducao,
          getLinhasDisponiveisPorTalhoes,
          insertRegColheitaPorPe,
          removeRegMdobrasColheitaPorPe,
        ],
      )}
    >
      {children}
    </RegColheitaPorPeContext.Provider>
  );
}

export function useRegColheitaPorPe() {
  const context = useContext(RegColheitaPorPeContext);
  if (!context) {
    throw new Error(
      'useRegColheitaPorPe must be used within a RegColheitaPorPeProvider',
    );
  }
  return context;
}

